import { UseFormRegisterReturn } from 'react-hook-form';
import { InputDropdown, InputDropdownItem, MultiSelectDropdown } from '../molecules';

type TimezonePickerProps = {
  timezone?: string;
  updateTimezone: (timezone: string) => void;
};

const getTimezoneOffsetInMinutes = (timeZoneIdentifier: string) => {
  const date = new Date();
  // Format the date to include the long offset name
  const formattedString = date.toLocaleString('en-US', {
    timeZone: timeZoneIdentifier,
    timeZoneName: 'longOffset',
  });

  // Extract the offset part (e.g., "+05:00" or "-07:00")
  const match = formattedString.match(/([+-]\d+):(\d+)$/);
  if (match) {
    return match[0];
  }
  return '+00:00';
};

const formatTimezoneLabel = (timeZone: string) => {
  const offset = getTimezoneOffsetInMinutes(timeZone);
  return `(GMT${offset}) ${timeZone}`;
};

const timeZoneOptions = Intl.supportedValuesOf('timeZone')
  .map((tz) => ({
    label: formatTimezoneLabel(tz),
    value: tz,
  }))
  .sort((a, b) => {
    const offsetA = a.label.match(/([+-]\d+):(\d+)/);
    const offsetB = b.label.match(/([+-]\d+):(\d+)/);
    if (offsetA && offsetB) {
      return parseInt(offsetA[0]) - parseInt(offsetB[0]);
    }
    return 0;
  });

// Underscores are replaced so typing e.g. "los angeles" matches America/Los_Angeles
const timeZoneDropdownItems: InputDropdownItem[] = timeZoneOptions.map((tz) => ({
  text: tz.label.replaceAll('_', ' '),
  textValue: tz.value,
}));

export const TimezonePicker = (props: TimezonePickerProps) => {
  const { timezone, updateTimezone } = props;
  // workaround for legacy user timezones that do not match Intl.supportedValuesOf
  // and will error if passed as timeZone option to date.toLocaleString
  // for these users we just do not populate
  const supportedTZFormat = !!timezone && Intl.supportedValuesOf('timeZone').includes(timezone);
  return (
    <InputDropdown
      type='default'
      label='Timezone'
      placeholder='Start typing a city or GMT offset'
      items={timeZoneDropdownItems}
      selectedKey={supportedTZFormat ? timezone : null}
      onSelectionChange={(key) => {
        // Clearing the input emits null; keep the previous timezone rather than unsetting it
        if (key !== null) updateTimezone(String(key));
      }}
    />
  );
};

type TimezoneMultiPickerProps = {
  updateTimezone: (timezone: string[]) => void;
  register: () => UseFormRegisterReturn<string>;
  filterable?: boolean;
  placeholder?: string;
  timezone?: string[];
};

export const TimezoneMultiPicker = (props: TimezoneMultiPickerProps) => {
  const { timezone, updateTimezone, register, placeholder, filterable } = props;

  return (
    <MultiSelectDropdown
      label='Timezone'
      options={timeZoneOptions.map((tz) => ({ label: tz.label }))}
      selectedItems={timezone ? timezone : []}
      onSelectionChange={(items) => updateTimezone(items)}
      register={register}
      placeholder={placeholder}
      filterable={filterable}
    />
  );
};
